import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { isServiceAuthDecision } from "../shared/constants";
import type {
  CloudflareAccessApplication,
  CloudflareMcpPortal,
  CloudflareMcpServer,
  CloudflareMcpServerSyncResult,
  CloudflareServiceToken,
} from "../shared/types";
import {
  adminQueries,
  adminQueryKeys,
  applyCloudflareMcpSyncResult,
  optimisticallySyncCloudflareMcpServer,
  restoreCloudflareMcpPages,
  useAdminApi,
} from "./admin-query";
import { CapabilityBadge, EmptyState } from "./AdminUi";
import { errorMessage } from "./api";
import { Icon } from "./Icon";

interface AuthorizationPath {
  label: string;
  tone: "positive" | "warning" | "neutral";
}

function normalizedEndpoint(value: string): string {
  if (!value.trim()) return "";
  try {
    const url = new URL(value.includes("://") ? value : `https://${value}`);
    const path = url.pathname.replace(/\/+$/, "").toLowerCase();
    return `${url.hostname.toLowerCase()}${path}`;
  } catch {
    return value
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/+$/, "");
  }
}

function matchingAccessApplication(
  applications: CloudflareAccessApplication[],
  type: "mcp" | "mcp_portal",
  name: string,
  endpoint: string,
): CloudflareAccessApplication | undefined {
  const normalized = normalizedEndpoint(endpoint);
  return applications.find(
    (application) =>
      application.type === type &&
      (normalizedEndpoint(application.domain) === normalized ||
        application.name.toLowerCase() === name.toLowerCase()),
  );
}

function authorizationPath(
  application: CloudflareAccessApplication | undefined,
  serviceTokens: CloudflareServiceToken[],
): AuthorizationPath {
  if (!application) {
    return { label: "Access app missing", tone: "warning" };
  }
  const human = application.policies.some(
    (policy) => policy.decision === "allow",
  );
  const serviceAuthPolicies = application.policies.filter((policy) =>
    isServiceAuthDecision(policy.decision),
  );
  const enabledTokenIds = new Set(
    serviceTokens.filter((token) => token.enabled).map((token) => token.id),
  );
  const matchingAgentToken = serviceAuthPolicies.some((policy) =>
    policy.include.some((rule) => {
      if ("any_valid_service_token" in rule) return enabledTokenIds.size > 0;
      const selector = rule.service_token;
      return (
        typeof selector === "object" &&
        selector !== null &&
        "token_id" in selector &&
        typeof selector.token_id === "string" &&
        enabledTokenIds.has(selector.token_id)
      );
    }),
  );
  if (human && matchingAgentToken) {
    return { label: "Human + agent", tone: "positive" };
  }
  if (matchingAgentToken) {
    return { label: "Agent only", tone: "positive" };
  }
  if (serviceAuthPolicies.length) {
    return { label: "Agent policy · token mismatch", tone: "warning" };
  }
  if (human) return { label: "Human only", tone: "neutral" };
  return { label: "No allow path", tone: "warning" };
}

function StatusBadge({ status }: { status: CloudflareMcpServer["status"] }) {
  const tone =
    status === "ready" ? "positive" : status === "waiting" ? "" : "warning";
  return (
    <span className={`panel-status${tone ? ` panel-status--${tone}` : ""}`}>
      {status}
    </span>
  );
}

function AccessPathBadge({ path }: { path: AuthorizationPath }) {
  return (
    <span
      className={`panel-status${
        path.tone === "neutral" ? "" : ` panel-status--${path.tone}`
      }`}
    >
      {path.label}
    </span>
  );
}

function portalEndpoint(portal: CloudflareMcpPortal): string {
  const hostname = portal.hostname
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "");
  return `https://${hostname}/mcp`;
}

function formatSyncDate(value?: string): string {
  if (!value) return "Never synced";
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "Sync time unknown";
  return `Synced ${new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp))}`;
}

export function CloudflareMcpSection({
  applications,
  serviceTokens,
}: {
  applications: CloudflareAccessApplication[];
  serviceTokens: CloudflareServiceToken[];
}) {
  const queryClient = useQueryClient();
  const adminApi = useAdminApi();
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(query.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [query]);

  const portalsQuery = useInfiniteQuery(
    adminQueries.cloudflareMcpPortals(20, search || undefined),
  );
  const serversQuery = useInfiniteQuery(
    adminQueries.cloudflareMcpServers(20, search || undefined),
  );
  const portalPages = portalsQuery.data?.pages ?? [];
  const serverPages = serversQuery.data?.pages ?? [];
  const portals = [
    ...new Map(
      portalPages
        .flatMap((page) => page.portals)
        .map((portal) => [portal.id, portal]),
    ).values(),
  ];
  const servers = [
    ...new Map(
      serverPages
        .flatMap((page) => page.servers)
        .map((server) => [server.id, server]),
    ).values(),
  ];
  const portalTotal = portalPages[0]?.total ?? portals.length;
  const serverTotal = serverPages[0]?.total ?? servers.length;
  const capability =
    portalPages[0]?.capability === "not_configured" &&
    serverPages[0]?.capability === "not_configured"
      ? "not_configured"
      : portalsQuery.isError || serversQuery.isError
        ? "degraded"
        : "available";
  const readyServers = servers.filter((server) => server.status === "ready");
  const exposedTools = portals.reduce(
    (count, portal) =>
      count +
      portal.servers.reduce(
        (serverCount, server) => serverCount + server.enabledToolCount,
        0,
      ),
    0,
  );

  const syncMutation = useMutation({
    mutationFn: async (server: CloudflareMcpServer) =>
      adminApi<{ sync: CloudflareMcpServerSyncResult }>(
        `/api/admin/cloudflare/mcp/servers/${encodeURIComponent(server.id)}/sync`,
        { method: "POST" },
      ),
    onMutate: async (server) => {
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: adminQueryKeys.cloudflareMcpPortals,
        }),
        queryClient.cancelQueries({
          queryKey: adminQueryKeys.cloudflareMcpServers,
        }),
      ]);
      return {
        snapshots: optimisticallySyncCloudflareMcpServer(
          queryClient,
          server.id,
        ),
      };
    },
    onError: (_caught, _server, context) => {
      if (context?.snapshots) {
        restoreCloudflareMcpPages(queryClient, context.snapshots);
      }
    },
    onSuccess: async ({ sync }, server) => {
      applyCloudflareMcpSyncResult(queryClient, server.id, sync);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: adminQueryKeys.cloudflareMcpPortals,
        }),
        queryClient.invalidateQueries({
          queryKey: adminQueryKeys.cloudflareMcpServers,
        }),
        queryClient.invalidateQueries({ queryKey: adminQueryKeys.security }),
      ]);
    },
  });

  const clearSearch = () => {
    setQuery("");
    setSearch("");
  };

  return (
    <section className="directory-card cloudflare-section mcp-control-section">
      <header className="collection-header">
        <div>
          <h2>MCP authorization</h2>
          <p>Portal reach, upstream health, tool exposure, and Access paths.</p>
        </div>
        <div className="collection-header__actions">
          <CapabilityBadge capability={capability} />
          <span className="collection-header__count">
            {portalTotal + serverTotal}
          </span>
        </div>
      </header>

      <div
        className="mcp-control-summary"
        aria-label="MCP authorization summary"
        role="group"
      >
        <div>
          <strong>{portalTotal}</strong>
          <span>portals</span>
        </div>
        <div>
          <strong>
            {readyServers.length}/{serverTotal}
          </strong>
          <span>servers ready</span>
        </div>
        <div>
          <strong>{exposedTools}</strong>
          <span>tools exposed</span>
        </div>
        <div>
          <strong>
            {serviceTokens.filter((token) => token.enabled).length}
          </strong>
          <span>active agent credentials</span>
        </div>
      </div>

      <div className="directory-toolbar mcp-control-toolbar">
        <label className="search-field">
          <span className="sr-only">Search MCP portals or servers</span>
          <Icon name="search" size={17} />
          <input
            aria-label="Search MCP portals or servers"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search portals or servers"
            type="search"
            value={query}
          />
        </label>
      </div>

      {(portalsQuery.isError || serversQuery.isError) && (
        <div
          className="notice notice--warning mcp-control-notice"
          role="status"
        >
          {portals.length || servers.length
            ? "Some MCP inventory could not be refreshed. Loaded data remains visible."
            : "MCP inventory is unavailable. Add MCP Portals Read to the Cloudflare API token."}
        </div>
      )}
      {syncMutation.isError && (
        <div className="notice notice--error mcp-control-notice" role="alert">
          {errorMessage(
            syncMutation.error,
            "Cloudflare could not synchronize this MCP server.",
          )}
        </div>
      )}

      <div className="mcp-inventory-grid">
        <section
          className="mcp-inventory-column"
          aria-labelledby="mcp-portals-heading"
        >
          <header>
            <div>
              <h3 id="mcp-portals-heading">Portals</h3>
              <p>Entry points and their effective server exposure.</p>
            </div>
            <span className="panel-status">{portalTotal}</span>
          </header>
          {portals.length ? (
            <ul className="mcp-resource-list">
              {portals.map((portal) => {
                const application = matchingAccessApplication(
                  applications,
                  "mcp_portal",
                  portal.name,
                  portal.hostname,
                );
                const path = authorizationPath(application, serviceTokens);
                return (
                  <li key={portal.id} className="mcp-resource-card">
                    <div className="mcp-resource-card__heading">
                      <span>
                        <strong>{portal.name}</strong>
                        <code>{portalEndpoint(portal)}</code>
                      </span>
                      <AccessPathBadge path={path} />
                    </div>
                    <div className="mcp-resource-meta">
                      <span>{portal.servers.length} servers</span>
                      <span>
                        Code mode: {portal.codeMode.replaceAll("_", " ")}
                      </span>
                      <span>
                        {portal.secureWebGateway
                          ? "Gateway egress"
                          : "Direct egress"}
                      </span>
                    </div>
                    {portal.servers.length ? (
                      <ul className="mcp-linked-server-list">
                        {portal.servers.map((server) => (
                          <li key={server.serverId}>
                            <span>
                              <strong>{server.name}</strong>
                              <small>
                                {server.onBehalf
                                  ? "Per-user OAuth"
                                  : "Portal credential"}
                              </small>
                            </span>
                            <span>
                              <small>
                                {server.enabledToolCount} of {server.toolCount}{" "}
                                tools
                              </small>
                              <StatusBadge status={server.status} />
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <small className="mcp-resource-card__empty">
                        No upstream servers linked.
                      </small>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : portalsQuery.isPending ? (
            <div className="mcp-column-state" role="status">
              <span className="spinner spinner--dark" /> Loading portals…
            </div>
          ) : (
            <EmptyState
              compact
              hint={
                search
                  ? "Try another portal or hostname."
                  : "Create a portal in Cloudflare Access AI controls."
              }
              onClearFilters={search ? clearSearch : undefined}
              title={search ? "No matching portals" : "No MCP portals"}
            />
          )}
          {portalsQuery.hasNextPage && (
            <footer className="mcp-column-footer">
              <button
                className="button button--quiet button--compact"
                disabled={portalsQuery.isFetchingNextPage}
                onClick={() => void portalsQuery.fetchNextPage()}
                type="button"
              >
                {portalsQuery.isFetchingNextPage
                  ? "Loading portals…"
                  : "Load more portals"}
              </button>
            </footer>
          )}
        </section>

        <section
          className="mcp-inventory-column"
          aria-labelledby="mcp-servers-heading"
        >
          <header>
            <div>
              <h3 id="mcp-servers-heading">Upstream servers</h3>
              <p>Capability sync, authentication, and Access enforcement.</p>
            </div>
            <span className="panel-status">{serverTotal}</span>
          </header>
          {servers.length ? (
            <ul className="mcp-resource-list">
              {servers.map((server) => {
                const application = matchingAccessApplication(
                  applications,
                  "mcp",
                  server.name,
                  server.hostname,
                );
                const path = authorizationPath(application, serviceTokens);
                const syncing =
                  syncMutation.isPending &&
                  syncMutation.variables?.id === server.id;
                return (
                  <li key={server.id} className="mcp-resource-card">
                    <div className="mcp-resource-card__heading">
                      <span>
                        <strong>{server.name}</strong>
                        <code>{server.hostname}</code>
                      </span>
                      <StatusBadge status={server.status} />
                    </div>
                    <div className="mcp-resource-meta">
                      <span>{server.authType}</span>
                      <span>
                        Auth: {server.authenticationStatus.replaceAll("_", " ")}
                      </span>
                      <span>
                        {server.secureWebGateway
                          ? "Gateway egress"
                          : "Direct egress"}
                      </span>
                    </div>
                    <div className="mcp-capability-counts">
                      <span>
                        <strong>{server.enabledToolCount}</strong> of{" "}
                        {server.toolCount} tools
                      </span>
                      <span>
                        <strong>{server.enabledPromptCount}</strong> of{" "}
                        {server.promptCount} prompts
                      </span>
                    </div>
                    {server.error && (
                      <small className="mcp-server-error">{server.error}</small>
                    )}
                    <footer className="mcp-resource-card__footer">
                      <span>
                        <AccessPathBadge path={path} />
                        <small>{formatSyncDate(server.lastSyncedAt)}</small>
                      </span>
                      <button
                        className="button button--secondary button--compact"
                        disabled={syncMutation.isPending}
                        onClick={() => {
                          syncMutation.reset();
                          syncMutation.mutate(server);
                        }}
                        type="button"
                      >
                        {syncing ? "Syncing…" : "Sync tools"}
                      </button>
                    </footer>
                  </li>
                );
              })}
            </ul>
          ) : serversQuery.isPending ? (
            <div className="mcp-column-state" role="status">
              <span className="spinner spinner--dark" /> Loading servers…
            </div>
          ) : (
            <EmptyState
              compact
              hint={
                search
                  ? "Try another server name."
                  : "Register an upstream server in Cloudflare Access AI controls."
              }
              onClearFilters={search ? clearSearch : undefined}
              title={search ? "No matching servers" : "No MCP servers"}
            />
          )}
          {serversQuery.hasNextPage && (
            <footer className="mcp-column-footer">
              <button
                className="button button--quiet button--compact"
                disabled={serversQuery.isFetchingNextPage}
                onClick={() => void serversQuery.fetchNextPage()}
                type="button"
              >
                {serversQuery.isFetchingNextPage
                  ? "Loading servers…"
                  : "Load more servers"}
              </button>
            </footer>
          )}
        </section>
      </div>
    </section>
  );
}
